import type { ProfitabilityRowDto } from '@hbs-us/contracts';
import { useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { isHidden, type ReportFormatters } from '../useReportFormatters.js';

/**
 * Job profitability table (legacy ReportsPage 252–328): a real semantic
 * <table> — Job · Client · Status · Revenue · Expenses · Labor · Profit ·
 * Margin · Owed — with a <caption>, <th scope="col"> headers, numeric columns
 * right-aligned, and a readable colspan empty row (A11Y-01 table semantics).
 *
 * Each row expands to a per-job cost drilldown (income / expense / labor / profit /
 * margin — all already on the row DTO) via an accessible toggle button
 * (`aria-expanded` + `aria-controls`); the drilldown renders in a full-width detail
 * row below the summary row.
 *
 * Numerals in IBM Plex Mono. Profit color by sign (green `--chart-3` / red
 * `--destructive`) — the sign on the number carries the meaning too. Owed in
 * amber `--chart-4`. Archived → `bg-secondary` badge; status → neutral badge.
 * `job_name` is the snake_case server field (faithful port).
 */

const NUMERIC_TH =
  'px-3 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide';
const TEXT_TH =
  'px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide';
const NUMERIC_TD = 'px-3 py-2 text-right font-mono text-foreground text-sm';

function Dash() {
  return <span className="text-muted-foreground">—</span>;
}

function DrilldownStat({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-border bg-card p-3">
      <dt className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </dt>
      <dd className={`font-mono font-medium text-foreground text-sm ${valueClassName ?? ''}`}>
        {value}
      </dd>
    </div>
  );
}

export function JobBreakdownTable({
  rows,
  fmt,
}: {
  rows: ProfitabilityRowDto[];
  fmt: ReportFormatters;
}) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggle = (id: number) => setExpandedId((current) => (current === id ? null : id));

  return (
    <section className="rounded-md border border-border bg-card p-6">
      <h2 className="mb-4 font-semibold text-foreground text-xl">{t('reports:table.heading')}</h2>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <caption className="sr-only">{t('reports:table.caption')}</caption>
          <thead>
            <tr className="border-border border-b">
              <th scope="col" className={TEXT_TH}>
                {t('reports:table.job')}
              </th>
              <th scope="col" className={TEXT_TH}>
                {t('reports:table.client')}
              </th>
              <th scope="col" className={TEXT_TH}>
                {t('reports:table.status')}
              </th>
              <th scope="col" className={NUMERIC_TH}>
                {t('reports:table.revenue')}
              </th>
              <th scope="col" className={NUMERIC_TH}>
                {t('reports:table.expenses')}
              </th>
              <th scope="col" className={NUMERIC_TH}>
                {t('reports:table.labor')}
              </th>
              <th scope="col" className={NUMERIC_TH}>
                {t('reports:table.profit')}
              </th>
              <th scope="col" className={NUMERIC_TH}>
                {t('reports:table.margin')}
              </th>
              <th scope="col" className={NUMERIC_TH}>
                {t('reports:table.owed')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-muted-foreground text-sm">
                  {t('reports:table.noJobs')}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const jobLabel = row.job_name || t('reports:table.unnamedJob', { id: row.id });
                const isExpanded = expandedId === row.id;
                const panelId = `job-drilldown-${row.id}`;
                return [
                  <tr key={row.id} className="border-border/60 border-b last:border-b-0">
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggle(row.id)}
                          aria-expanded={isExpanded}
                          aria-controls={panelId}
                          aria-label={t(
                            isExpanded ? 'reports:table.collapse' : 'reports:table.expand',
                            { job: jobLabel },
                          )}
                          className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {isExpanded ? (
                            <ChevronDown className="size-4" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="size-4" aria-hidden="true" />
                          )}
                        </button>
                        <Link
                          to="/jobs"
                          className="font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        >
                          {jobLabel}
                        </Link>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-foreground text-sm">{row.client || <Dash />}</td>
                    <td className="px-3 py-2 text-sm">
                      {row.archived ? (
                        <span className="inline-flex items-center rounded-md bg-secondary px-2 py-0.5 text-secondary-foreground text-xs">
                          {t('reports:table.archived')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground text-xs">
                          {row.status || '—'}
                        </span>
                      )}
                    </td>
                    <td className={NUMERIC_TD}>
                      {row.income > 0 ? fmt.money(row.income) : <Dash />}
                    </td>
                    <td className={NUMERIC_TD}>
                      {row.expenses > 0 ? fmt.money(row.expenses) : <Dash />}
                    </td>
                    <td className={NUMERIC_TD}>
                      {row.labor > 0 ? fmt.money(row.labor) : <Dash />}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono font-semibold text-sm ${
                        isHidden(row.profit as number | null)
                          ? undefined
                          : row.profit >= 0
                            ? 'text-success'
                            : 'text-destructive'
                      }`}
                    >
                      {fmt.money(row.profit)}
                    </td>
                    <td className={NUMERIC_TD}>{fmt.percent(row.margin)}</td>
                    <td className="px-3 py-2 text-right font-mono text-sm">
                      {row.openAr > 0 ? (
                        <span className="font-semibold text-warning">{fmt.money(row.openAr)}</span>
                      ) : (
                        <Dash />
                      )}
                    </td>
                  </tr>,
                  isExpanded ? (
                    <tr
                      key={`${row.id}-drilldown`}
                      className="border-border/60 border-b bg-muted/30"
                    >
                      <td colSpan={9} className="px-3 py-4">
                        <dl
                          id={panelId}
                          className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5"
                        >
                          <DrilldownStat
                            label={t('reports:table.drilldownIncome')}
                            value={fmt.money(row.income)}
                          />
                          <DrilldownStat
                            label={t('reports:table.drilldownExpense')}
                            value={fmt.money(row.expenses)}
                          />
                          <DrilldownStat
                            label={t('reports:table.drilldownLabor')}
                            value={fmt.money(row.labor)}
                          />
                          <DrilldownStat
                            label={t('reports:table.drilldownProfit')}
                            value={fmt.money(row.profit)}
                            valueClassName={
                              isHidden(row.profit as number | null)
                                ? undefined
                                : row.profit >= 0
                                  ? 'text-success'
                                  : 'text-destructive'
                            }
                          />
                          <DrilldownStat
                            label={t('reports:table.drilldownMargin')}
                            value={fmt.percent(row.margin)}
                          />
                        </dl>
                      </td>
                    </tr>
                  ) : null,
                ];
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
